/**
 * The confirmation every user data change goes through: what is about to happen, any inputs the
 * change needs, and a required reason.
 *
 * The server refuses a change without a reason, so the dialog refuses first — the admin is told
 * at the field, not by a 400. The reason goes on the user's audit history, which is why it is
 * asked for on edits as well as removals.
 *
 * Mount it only while it is needed (`{target && <UserDataActionDialog ... />}`): every open then
 * starts with an empty reason, and one change's reason can never be carried into the next.
 */

import { ReactNode, useState } from 'react'
import { Alert, TextField } from '@mui/material'
import { ConfirmDialog } from '@components/common/ConfirmDialog'

/** Matches the server's column width for every reason. */
export const REASON_MAX_LENGTH = 500

export interface UserDataActionDialogProps {
  title: string
  /** The consequence, in a sentence or two. Rendered inside a <p>, so inline content only. */
  message?: ReactNode
  confirmLabel: string
  destructive?: boolean
  /** Inputs the change needs, rendered between the message and the reason. */
  children?: ReactNode
  /** The reason may be left empty (e.g. unhiding a review). Required by default. */
  reasonOptional?: boolean
  /** Checked on confirm, after the reason. Return a message to refuse, or null. */
  validate?: () => string | null
  pending: boolean
  /** The server's refusal, if the last attempt failed. */
  error: string | null
  onSubmit: (reason: string) => void
  onClose: () => void
}

export function UserDataActionDialog({
  title,
  message,
  confirmLabel,
  destructive = false,
  children,
  reasonOptional = false,
  validate,
  pending,
  error,
  onSubmit,
  onClose,
}: UserDataActionDialogProps) {
  const [reason, setReason] = useState('')
  const [attempted, setAttempted] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const trimmed = reason.trim()
  const reasonMissing = !reasonOptional && attempted && trimmed.length === 0

  const confirm = () => {
    setAttempted(true)
    if (!reasonOptional && trimmed.length === 0) return
    const invalid = validate?.() ?? null
    setProblem(invalid)
    if (!invalid) onSubmit(trimmed)
  }

  const shownError = problem ?? error

  return (
    <ConfirmDialog
      open
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      destructive={destructive}
      loading={pending}
      onConfirm={confirm}
      onClose={onClose}
    >
      {shownError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {shownError}
        </Alert>
      )}

      {children}

      <TextField
        label={reasonOptional ? 'Reason (optional)' : 'Reason'}
        placeholder="Why — recorded on the user's audit history"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        required={!reasonOptional}
        fullWidth
        multiline
        minRows={2}
        autoFocus={!children}
        error={reasonMissing}
        helperText={reasonMissing ? 'A reason is required' : `${reason.length}/${REASON_MAX_LENGTH}`}
        inputProps={{ maxLength: REASON_MAX_LENGTH }}
        sx={{ mt: 2 }}
      />
    </ConfirmDialog>
  )
}
